// src/components/map/components/AccountsListPane.js
import React from "react";
import { Package } from "lucide-react";

export default function AccountsListPane({
  points,
  loading,
  error,
  selectedPointId,
  onSelect,
  users,
  territories,
  filters,
  onFiltersChange,
  getUserLabel,
  isAdmin,
}) {
  const handleFilterChange = (field, value) => {
    onFiltersChange((prev) => ({ ...prev, [field]: value }));
  };

  const renderBadge = (p) => {
    const converted = Number(p.convertedCount) || 0;
    const samples = Number(p.sampleCount) || 0;

    if (converted > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 whitespace-nowrap border border-green-200">
          <Package className="w-3 h-3" />
          {converted}
        </span>
      );
    }

    if (samples > 0) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700 whitespace-nowrap border border-blue-200">
          <Package className="w-3 h-3" />
          {samples}
        </span>
      );
    }

    return null;
  };

  return (
    <div className="flex flex-col h-full">
      <div className="px-2 py-2 border-b border-gray-200 text-[11px] space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin ? (
            <label className="flex items-center gap-1 text-gray-600">
              <span className="whitespace-nowrap">End user</span>
              <select
                className="border border-gray-300 rounded px-1.5 py-0.5 text-[11px] bg-white"
                value={filters.endUserId}
                onChange={(e) => handleFilterChange("endUserId", e.target.value)}
              >
                <option value="all">All</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {getUserLabel(u)}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          <label className="flex items-center gap-1 text-gray-600">
            <span className="whitespace-nowrap">Territory</span>
            <select
              className="border border-gray-300 rounded px-1.5 py-0.5 text-[11px] bg-white max-w-[130px]"
              value={filters.territoryId}
              onChange={(e) => handleFilterChange("territoryId", e.target.value)}
            >
              <option value="all">All</option>
              {territories.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || "Untitled territory"}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex items-center gap-1 text-gray-600">
          <label className="inline-flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              className="h-3 w-3"
              checked={filters.samplesOnly}
              onChange={(e) => handleFilterChange("samplesOnly", e.target.checked)}
            />
            <span>Accounts with sample orders</span>
          </label>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 text-xs px-2 py-1.5">{error}</div>}

      {loading ? (
        <div className="px-3 py-2 text-xs text-gray-500">Loading accounts…</div>
      ) : points.length === 0 ? (
        <div className="px-3 py-2 text-xs text-gray-500">No accounts match the current filters.</div>
      ) : (
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {points.map((p) => {
            const isSelected = p.id === selectedPointId;

            return (
              <button
                key={p.id}
                type="button"
                onClick={() => onSelect(p.id)}
                className={`w-full text-left px-2 py-1.5 rounded-md border text-xs transition-colors ${
                  isSelected
                    ? "bg-blue-50 border-blue-300 text-blue-900"
                    : "bg-white border-transparent text-gray-800 hover:bg-gray-50 hover:border-gray-200"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{p.name || "Unnamed account"}</span>
                  {renderBadge(p)}
                </div>

                <div className="text-[11px] text-gray-500 truncate">
                  {[p.city, p.state].filter(Boolean).join(", ")}
                  {p.address ? ` • ${p.address}` : ""}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
